import { Disclosure } from '../ui/Disclosure.js';
import { markPaint, type MarkStyle } from './marks.js';

/**
 * WHAT THE MARKS IN THE SCRIPT MEAN, under the category chips.
 *
 * The chips say what a mark's COLOUR means; this says what its SHAPE means, which is the part
 * nobody guesses: a name marked by a dotted underline was found by its name alone, and reads as a
 * rendering fault until something says so. Each sample is drawn by the reader's own class and
 * `markPaint`, so the key cannot drift from the script it explains.
 *
 * Open until the person closes it; the choice is kept per browser like the reader's other
 * preferences. The reader renders it only while there are highlights to explain.
 */
interface Entry {
  readonly testId: string;
  readonly style: MarkStyle;
  readonly selected?: boolean;
  readonly more?: string;
  /** The shape in words, for a screen reader: the sample itself is hidden from it. */
  readonly shape: string;
  readonly label: string;
  readonly meaning: string;
}

const ENTRIES: readonly Entry[] = [
  { testId: 'sr-key-FILLED', style: 'FILLED', shape: 'Filled', label: 'On the sheet', meaning: 'At its exact words' },
  { testId: 'sr-key-OUTLINED', style: 'OUTLINED', shape: 'Outlined', label: 'Awaiting your decision', meaning: 'At its exact words' },
  { testId: 'sr-key-DASHED', style: 'DASHED', shape: 'Dashed outline', label: 'Check the place', meaning: 'Found elsewhere, or repeated' },
  { testId: 'sr-key-DOTTED', style: 'DOTTED', shape: 'Dotted underline', label: 'Approximate match', meaning: 'By its name only' },
  { testId: 'sr-key-selected', style: 'FILLED', selected: true, shape: 'Dark ring', label: 'Open on the right', meaning: 'The item you picked' },
  { testId: 'sr-key-overlap', style: 'OUTLINED', more: '+2', shape: 'A small +2', label: 'Several items', meaning: 'Click to choose one' },
];

/** A neutral ink, so the samples teach the shape and leave colour to the chips. */
const SAMPLE_COLOUR = '#475467';

export function MarkKey({ open, onToggle }: { open: boolean; onToggle: (open: boolean) => void }) {
  return (
    <Disclosure
      summary="What the marks mean"
      meta="Colour is the category"
      defaultOpen={open}
      onToggle={onToggle}
      className="sr-key"
      testId="sr-key"
    >
      <dl className="sr-key-list">
        {ENTRIES.map((entry) => (
          <div key={entry.testId} className="sr-key-entry" data-testid={entry.testId}>
            <dt>
              <span
                className="sr-mark sr-key-sample"
                data-style={entry.style}
                data-selected={entry.selected === true ? '1' : '0'}
                style={markPaint(entry.style, SAMPLE_COLOUR)}
                aria-hidden="true"
              >
                Name
                {entry.more === undefined ? null : <sup className="sr-mark-more">{entry.more}</sup>}
              </span>
              <span className="sr-only">{entry.shape}</span>
            </dt>
            <dd>
              <strong>{entry.label}</strong>
              <span className="sr-key-meaning">{entry.meaning}</span>
            </dd>
          </div>
        ))}
      </dl>
    </Disclosure>
  );
}
